import {
  Typography,
  Button,
  Box,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  CircularProgress,
} from '@mui/material';
import React, { SetStateAction, useState } from 'react';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { IProfile } from '../../../global/interfaces';
import { StyledModal, useDocumentTemplateStyles } from './styles';

import { IPlan } from '../../../global/interfaces';

interface IProps {
  profile: IProfile | undefined;
  plans: IPlan[];
  setPlans: (plans: IPlan[]) => void;
  updatePlan: (plan: IPlan) => Promise<void>;
  isPlansModalOpen: boolean;
  handleClosePlansModal: React.Dispatch<SetStateAction<boolean>>;
}

const PlansModal = ({
  profile,
  plans,
  setPlans,
  updatePlan,
  isPlansModalOpen,
  handleClosePlansModal,
}: IProps) => {
  const styles = useDocumentTemplateStyles();
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [uploadedPlan, setUploadedPlan] = useState<IPlan | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const downloadTemplate = () => {
    const template = plans.find((t) => t.name === selectedPlanName);

    const dataStr = JSON.stringify(template, null, 2);
    const dataBlob = new Blob([dataStr], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${template?.name.replace(/\s+/g, '_')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const uploadTemplate = (file: File | undefined) => {
    if (!file) return;

    setIsSaving(false);
    setUploadedPlan(null);
    setSelectedPlanName('');

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const plan = JSON.parse(event.target?.result as string);
        plan.dtid = 0;
        plan.user_id = profile?.user_id ?? 0;

        let counter = 1;
        let newTemplateName = plan.template_name;

        while (plans.some((t) => t.name === newTemplateName)) {
          newTemplateName = `${plan.name}_${counter}`;
          counter++;
        }

        plan.template_name = newTemplateName;
        plan.title = plan.title + (counter > 1 ? ` ${counter - 1}` : '');

        setUploadedPlan(plan);
        setPlans([...plans, plan]);
        setSelectedPlanName(plan.plan_name);
        console.log('Uploaded plans:', plan);
      } catch (err) {
        console.error('Error parsing JSON:', err);
      }
    };
    reader.readAsText(file);
  };
  const saveTemplate = async () => {
    if (!uploadedPlan) return;

    try {
      setIsSaving(true);
      await updatePlan(uploadedPlan);
      setUploadedPlan(null);
      setIsSaving(false);
    } catch (error: any) {
      console.error('Save template error:', error);
      setIsSaving(false);
    }
  };

  const dropTemplate = () => {
    if (!uploadedPlan) return;

    const filteredTemplates = plans.filter(
      (template) => template !== uploadedPlan,
    );
    setPlans(filteredTemplates);
    setUploadedPlan(null);
  };

  return (
    <Modal open={isPlansModalOpen} onClose={() => handleClosePlansModal(false)}>
      <StyledModal sx={{ maxHeight: 'min(75rem, calc(100vh - 5rem))' }}>
        <FormControl fullWidth sx={{ mt: '2.5rem' }}>
          <InputLabel sx={styles.inputLabel}>Select Plan</InputLabel>
          <Select
            value={selectedPlanName}
            onChange={(e) => setSelectedPlanName(e.target.value)}
            sx={styles.select}
            variant="outlined">
            {plans.map((plan) => (
              <MenuItem key={plan.name} value={plan.name}>
                {plan.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="subtitle2" sx={{ mt: '2rem' }}>
          Plan Data:
        </Typography>
        <Box sx={styles.planData}>
          <pre
            style={{
              padding: 0,
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              flexGrow: 1,
              overflowY: 'auto',
            }}>
            {JSON.stringify(
              plans.find((t) => t.name === selectedPlanName),
              null,
              2,
            )}
          </pre>
        </Box>
        <Box sx={styles.buttonContainer}>
          {uploadedPlan && (
            <>
              <Button variant="outlined" color="error" onClick={dropTemplate}>
                {`Drop ${uploadedPlan.name}`}
              </Button>{' '}
              <Button
                variant="contained"
                color="primary"
                onClick={saveTemplate}
                disabled={isSaving}>
                {isSaving ? (
                  <>
                    <CircularProgress size={'1.25rem'} sx={{ mr: '0.5rem' }} />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </>
          )}
          {false && (
            <>
              {selectedPlanName && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Are you sure you want to delete "${selectedPlanName}"?`,
                      )
                    ) {
                      alert('This function is not implemented yet');
                      console.log('Delete template:', selectedPlanName);
                    }
                  }}
                  startIcon={<DeleteOutlineIcon />}
                  sx={{ mr: 1 }}>
                  Delete Plan
                </Button>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<DescriptionOutlinedIcon />}>
                Upload Plan
                <input
                  type="file"
                  hidden
                  accept=".json"
                  onChange={(e) => uploadTemplate(e.target.files?.[0])}
                />
              </Button>

              <Button
                variant="contained"
                onClick={downloadTemplate}
                startIcon={<DescriptionOutlinedIcon />}>
                Download Plan
              </Button>
            </>
          )}
        </Box>
      </StyledModal>
    </Modal>
  );
};

export default PlansModal;
